import { Box, Button, FormHelperText, TextField, Typography } from "@mui/material";
import { FC } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import CharacterCount from "../../components/CharacterCount";
import { MainContainer } from "../../components/MainContainerComp";
import { config } from "../../utils/config";
import { ITheme } from "../../utils/theme";
import { asteriskStyle, successMsgStyle } from "./styled";

export interface ContactUsProps {
  name: string;
  email: string;
  regId?: number | '';
  onSetName: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSetEmail: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSetRegId: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSubmit: () => void;
  isFormTouched: boolean;
  isValidEmail: (email: string) => boolean;
  onSetCaptchaResponse: (captchaResponse: string | null) => void;
  captchaResponse: string;
  comments: string;
  onSetComments: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  showSuccessMsg: boolean;
}

export const ContactUs: FC<ContactUsProps> = ({
  name,
  email,
  regId,
  onSetEmail,
  onSetName,
  onSetRegId,
  onSubmit,
  isFormTouched,
  isValidEmail,
  onSetCaptchaResponse,
  captchaResponse,
  comments,
  onSetComments,
  showSuccessMsg
}) => {
  const nameError = !name.trimEnd() && isFormTouched;
  const tokenError = !captchaResponse && isFormTouched;
  const emailError = (!email && isFormTouched) || (!!email && !isValidEmail(email));
  const commentsError = !comments.trimEnd() && isFormTouched;
  const disabled = !name.trimEnd() || !captchaResponse || !email || !isValidEmail(email) || !comments.trimEnd();
  return (
    <MainContainer>
      <Box>
        {
          showSuccessMsg ? <Box sx={successMsgStyle}>
            <Typography sx={{ mb: 2 }} data-automation="contact-sent-txt">Your message has been sent!</Typography>
            <Typography data-automation="contact-sent-txt">Please use the menu to navigate to other application screens.</Typography>
          </Box> :
            <>
              <Typography sx={{ fontWeight: 'bold', pb: 1 }} data-automation="description-text">Contact BCMEA for Questions, Comments, and Feedback.</Typography>
              <Typography sx={{ pb: 1 }} data-automation="description-text">
                If you have any questions, comments or feedback, we are interested in hearing from you.
              </Typography>
              <Typography sx={{ pb: 1 }} data-automation="description-text">
                If you have any enhancements or new ideas that you would like to see on this application, please
                make sure that you tell us about them.
              </Typography>
              <Typography sx={{ pb: 2 }} data-automation="description-text">
                Please enter your comment below, and tick the "I'm not a robot" checkbox to enable the
                <Typography component='span' sx={{ fontWeight: 'bold' }}> Submit</Typography> button:
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant='form-label' data-automation="name-lbl">Name<Typography component={"span"} sx={asteriskStyle}>&nbsp;*</Typography></Typography>
                <TextField
                  fullWidth
                  value={name}
                  onChange={onSetName}
                  placeholder="Enter your name"
                  error={nameError}
                  inputProps={{
                    maxLength: 100,
                  }}
                  data-automation="name-txt"
                />
                <FormHelperText 
                  component={'div'}
                  data-automation="mandatory-field-lbl"
                  error={nameError}
                >
                  {nameError ? 
                    "Name field is mandatory" : <CharacterCount currentLength={name.length} maxLength={100} />
                  }
                </FormHelperText>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant='form-label' data-automation="reg-lbl">Employee #</Typography>
                <TextField
                  fullWidth
                  value={regId ?? ''}
                  onChange={onSetRegId}
                  placeholder="Enter your employee number"
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: 6,
                  }}
                  data-automation="reg-lbl"
                />
                <FormHelperText 
                  component={'div'}
                >
                  <CharacterCount currentLength={(regId?.toString().length || 0)} maxLength={6} />
                </FormHelperText>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant='form-label' data-automation="email-lbl">Email<Typography component={"span"} sx={asteriskStyle}>&nbsp;*</Typography></Typography>
                <TextField
                  fullWidth
                  value={email}
                  onChange={onSetEmail}
                  placeholder="Enter your email"
                  error={emailError}
                  inputProps={{
                    maxLength: 100,
                    autoCapitalize: 'none',
                  }}
                  data-automation="email-lbl"
                />
                <FormHelperText 
                  component={'div'}
                  data-automation="invalid-email-lbl"
                  error={emailError}
                >
                  {emailError ? 
                      "Invalid Email Address" : <CharacterCount currentLength={email.length} maxLength={100} />
                  }
                </FormHelperText>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant='form-label' data-automation="comments-lbl">Comments<Typography component={"span"} sx={asteriskStyle}>&nbsp;*</Typography></Typography>
                <TextField
                  rows={10}
                  fullWidth
                  multiline
                  value={comments}
                  onChange={onSetComments}
                  placeholder="Enter your comments"
                  error={commentsError}
                  inputProps={{
                    maxLength: 500,
                  }}
                  data-automation="comments-lbl"
                />
                <FormHelperText 
                  component={'div'}
                  data-automation="mandatory-field-lbl"
                  error={commentsError}
                >
                  {commentsError ? 
                      "Comments field is mandatory" : <CharacterCount currentLength={comments.length} maxLength={500} />
                  }
                </FormHelperText>
              </Box>
              <Box sx={{ mb: 2 }}>
                <ReCAPTCHA
                  sitekey={config.recaptchaSiteKey}
                  onChange={onSetCaptchaResponse}
                  onExpired={() => onSetCaptchaResponse('')}
                  data-automation="captcha-frame"
                />
                <FormHelperText 
                  component={'div'}
                  data-automation="require-captcha-lbl"
                  error={tokenError}
                >
                  {tokenError && "Captcha validation is required"}
                </FormHelperText>
              </Box>
              <Box sx={{ textAlign: 'right', mb: 1 }}>
                <Button
                  disabled={disabled}
                  variant="contained"
                  onClick={onSubmit}
                  sx={{ fontWeight: 'bold' }}
                  data-automation="submit-btn"
                >
                  Submit
                </Button>
              </Box>
            </>
        }
      </Box>
    </MainContainer>
  )
};